import { untracked } from "@suse-o11y/signal";
import type { Store } from "redux";
import { z } from "zod";

import {
	Mode,
	RunMode,
	type ServerSuppliedWebUIConfig,
	type SubscriptionState,
	isLicensedSubscription,
} from "~/models/dto";
import { differenceInSeconds } from "~/tools/date-time";
import { IS_USER_AUTHENTICATED, LOGIN_REDIRECT_DEEP_LINK_HASH, LocalStorage } from "~/tools/local-storage";
import { Logger } from "~/tools/logger";

import { Messages } from "../common/Messages";
import { request } from "../generic/request";

import { setUiConfig } from "./state/config/ConfigActions";
import type { RootState } from "./state/RootState";
import { usageTrack } from "./state/usage-track/UsageTrackActions";

const logger = Logger.namespace("BootLoaderService");

// Grab the current location hash before any changes can be made by routers
// This is needed for restoring it after an authentication flow
const hashFromUrl = location.hash;

const onStartFinished = (config: ServerSuppliedWebUIConfig, log: Logger): void => {
	// biome-ignore lint/style/noRestrictedGlobals: legacy
	const { startTime = 0 } = window as { startTime?: number }; // set in app.tsx
	const bootTime = performance.now() - startTime;
	const { runMode } = config;
	const runModeMsg = runMode !== RunMode.Normal ? `Running in ${runMode.toLowerCase()} mode.` : "";
	const startupMsg = `\nStackState web-ui loaded in ${bootTime}ms. ${runModeMsg}`;
	log.info(startupMsg);
};

const trackUserLogin = async (isGuiAuth: boolean, reduxStore: Store<RootState>): Promise<void> => {
	if (isGuiAuth && untracked((monitor) => !LocalStorage.getItem(monitor, IS_USER_AUTHENTICATED, z.boolean()))) {
		LocalStorage.setItem(IS_USER_AUTHENTICATED, true);
		reduxStore.dispatch(usageTrack({ name: "LAST_TIME_LOGGED", value: new Date(), mode: Mode.Set }));
		reduxStore.dispatch(usageTrack({ name: "NUMBER_OF_TIMES_LOGGED", value: 1, mode: Mode.Increment }));
	}
};

export const bootLoad = async (reduxStore: Store<RootState>): Promise<void> => {
	logger.info("Booting the app..");

	try {
		const config = await request<ServerSuppliedWebUIConfig>("/api/server/webuiconfig");

		logger.info("Starting with config:", config);

		// Read the saved redirect URL and clear it immediately
		const redirectHashUrl = untracked((monitor) => LocalStorage.getItem(monitor, LOGIN_REDIRECT_DEEP_LINK_HASH));
		LocalStorage.setItem(LOGIN_REDIRECT_DEEP_LINK_HASH, "");

		if (redirectHashUrl) {
			location.hash = redirectHashUrl;
		}

		const { isGuiAuth } = config;

		reduxStore.dispatch(setUiConfig(config));

		if (isSubscriptionValid(config.subscription)) {
			await trackUserLogin(isGuiAuth, reduxStore);
		}

		onStartFinished(config, logger);
	} catch (error) {
		logger.error(Messages.errorStartingStackState(), error);

		// if anything fails - keep the login redirect to return back to the same URL
		LocalStorage.setItem(LOGIN_REDIRECT_DEEP_LINK_HASH, hashFromUrl);

		// if anything goes wrong we stop the execution
		return Promise.reject(error);
	}
};

function isSubscriptionValid(subscription: SubscriptionState): boolean {
	if (!isLicensedSubscription(subscription)) {
		return false;
	}

	if (subscription.subscription.expiryTimestampMs == null) {
		return true;
	}

	return differenceInSeconds(subscription.subscription.expiryTimestampMs, Date.now()) > -1;
}
